/**
 * Frame — the top-level layout shell wrapping every page.
 *
 * Renders the Bulma navbar with: logo (home link), project title/type tags,
 * auto-save status indicator, dark/light theme toggle, version badge, and
 * user menu with sign-out. The `children` prop renders the page content below.
 *
 * Accepts an optional `onNewProject` callback — when provided, a "New Project"
 * button appears in the navbar (used on the Projects list page).
 */
/* eslint-disable jsx-a11y/anchor-is-valid */
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
// --------------------------------------------------------------
import { signout } from "Redux/user/actions";
// --------------------------------------------------------------
import logo from "Media/accent3.png";
import ChatPanel from "Components/ChatPanel";
import ScenarioToggle from "Components/ScenarioToggle";
import ChatHistoryModal from "Components/ChatHistoryModal";

// Reads persisted theme preference from localStorage, defaulting to "dark"
const getInitialTheme = () => {
  const saved = localStorage.getItem("ev-theme");
  if (saved) return saved;
  return "dark";
};

// --------------------------------------------------------------
// --------------------------------------------------------------
const Frame = ({ children, onNewProject }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [theme, setTheme] = useState(getInitialTheme);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("ev-theme", theme);
  }, [theme]);

  const project = useSelector((state) => state.project);
  const { title, projectType } = project;
  const activeScenario = useSelector((s) => s.data.activeScenario) || "base";
  const activeScenarioName = (project.scenarios || []).find((s) => s.id === activeScenario)?.name;
  const inScenario = activeScenario !== "base";
  const chatCount = useSelector((s) => s.chats.list.length);
  const [showChats, setShowChats] = useState(false);
  const { version, email, saving, notification } = useSelector(
    (state) => state.user
  );

  const navigateHome = () => {
    dispatch({ type: "CLEAR_PROJECT" });
    navigate("/");
  };

  const toggleTheme = () => {
    setTheme((t) => (t === "light" ? "dark" : "light"));
  };

  return (
    <div className="padding-8">
      {notification && (
        <div className="notifier-wrapper">
          <div className="notification is-success is-light">{notification}</div>
        </div>
      )}
      <nav className="navbar" role="navigation" aria-label="main navigation">
        <div className="navbar-brand">
          <div className="navbar-item is-clickable" onClick={navigateHome}>
            <img src={logo} width="115" height="28" alt="Logo" />
          </div>
        </div>

        <div id="navbarBasicExample" className="navbar-menu">
          <div className="navbar-start">
            {onNewProject && (
              <div className="new-button">
                <button className="button is-small" onClick={onNewProject}>
                  <span className="icon">
                    <i className="is-large fa-duotone fa-circle-plus" />
                  </span>
                  <span>New Project</span>
                </button>
              </div>
            )}
          </div>
          {!onNewProject && title && (
            <div className="navbar-item">
              <div className="tags has-addons are-large">
                <div className="tag is-primary is-light">{title}</div>
                <div className="tag is-light">{projectType}</div>
              </div>
            </div>
          )}
          {!onNewProject && title && (
            <div className="navbar-item">
              <ScenarioToggle />
            </div>
          )}
          <div className="navbar-end">
            {title && chatCount > 0 && (
              <div className="navbar-item">
                <button
                  className="button is-small"
                  onClick={() => setShowChats(true)}
                  title="Saved chats"
                  style={{ background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.25)", color: "#fff", borderRadius: "8px", fontWeight: 700 }}
                >
                  <i className="fa-duotone fa-comments" style={{ marginRight: "0.4rem" }} />
                  Chats
                  <span className="tag is-small" style={{ marginLeft: "0.4rem", backgroundColor: "rgba(255,255,255,0.25)", color: "#fff", borderRadius: "4px" }}>
                    {chatCount}
                  </span>
                </button>
              </div>
            )}
            {title ? (
              <div
                className="navbar-item activity-indicator"
                style={{ visibility: saving === null ? "hidden" : "visible" }}
              >
                {saving ? (
                  <div>
                    <span>
                      <i className="fa-solid fa-spinner fa-spin"></i>
                    </span>
                    &nbsp;&nbsp;
                    <span>Saving project...</span>
                  </div>
                ) : (
                  <div>
                    <span>
                      <i className="fa-solid fa-check"></i>
                    </span>
                    &nbsp;&nbsp;
                    <span>Project saved</span>
                  </div>
                )}
              </div>
            ) : null}

            <div className="navbar-item">
              <button className="theme-toggle" onClick={toggleTheme}>
                <i className={theme === "light" ? "fa-duotone fa-moon" : "fa-duotone fa-sun"} />
                <span style={{ fontSize: "0.7rem", fontWeight: 700 }}>
                  {theme === "light" ? "Dark" : "Light"}
                </span>
              </button>
            </div>

            <div className="navbar-item">
              <div className="tag">V{version}</div>
            </div>
            <div className="navbar-item has-dropdown is-hoverable">
              <a role="button" tabIndex={0} className="navbar-link">{email.toLowerCase()}</a>

              <div className="navbar-dropdown">
                <a
                  role="button"
                  tabIndex={0}
                  className="navbar-item"
                  onClick={() => {
                    console.log("Signing out");
                    dispatch(signout());
                  }}
                >
                  Sign Out
                </a>
              </div>
            </div>
          </div>
        </div>
      </nav>
      <div className="box main-container">{children}</div>
      {inScenario && (
        <div
          style={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 30, // above content, below the chat panel/button and modals
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            padding: "0.45rem 84px 0.45rem 1rem", // right pad clears the chat button
            backgroundColor: "#FBEFD8",
            color: "#8A5A00",
            borderTop: "3px solid #E8A33D",
            boxShadow: "0 -2px 10px rgba(0,0,0,0.14)",
            fontSize: "0.8rem",
            fontWeight: 600,
          }}
        >
          <i className="fa-duotone fa-flask" />
          Viewing scenario: {activeScenarioName || activeScenario} — these are what-if numbers, not the original deal.
        </div>
      )}
      {project.id && <ChatPanel />}
      {showChats && <ChatHistoryModal onClose={() => setShowChats(false)} />}
    </div>
  );
};

export default Frame;
